import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

/**
 * Theme provider with three modes: "light" | "dark" | "system".
 *
 * - Persists selection to localStorage under `flex-theme`.
 * - When mode is "system", auto-follows OS `prefers-color-scheme` changes.
 * - When mode is "light" or "dark", user choice wins over OS.
 */
const ThemeContext = createContext(null);
const STORAGE_KEY = "flex-theme"; // values: "light" | "dark" | "system"

function detectSystem() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function readStoredMode() {
  if (typeof window === "undefined") return "system";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : "system";
  } catch {
    return "system";
  }
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStoredMode); // "light" | "dark" | "system"
  const [systemTheme, setSystemTheme] = useState(detectSystem);

  // Track OS preference for "system" mode.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemTheme(e.matches ? "dark" : "light");
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const effectiveTheme = mode === "system" ? systemTheme : mode;

  // Apply .dark class on <html>.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (effectiveTheme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
  }, [effectiveTheme]);

  const setMode = useCallback((next) => {
    setModeState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  // Simple toggle → cycles Light ↔ Dark (leaves "system" only if explicitly chosen).
  const toggle = useCallback(() => {
    setMode(effectiveTheme === "dark" ? "light" : "dark");
  }, [effectiveTheme, setMode]);

  const value = useMemo(
    () => ({
      mode,                 // user selection: "light" | "dark" | "system"
      theme: effectiveTheme, // resolved theme actually applied
      isDark: effectiveTheme === "dark",
      setMode,
      toggle,
    }),
    [mode, effectiveTheme, setMode, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) return { mode: "system", theme: "light", isDark: false, setMode: () => {}, toggle: () => {} };
  return ctx;
};
